import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Trash2, Plus, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  getMailboxes,
  addMailbox,
  deleteMailbox,
  testMailbox,
  pollEmailNow,
} from "@/lib/api";

const PROVIDERS = [
  { value: "gmail", label: "Gmail" },
  { value: "yahoo", label: "Yahoo" },
  { value: "outlook", label: "Outlook / Hotmail" },
];

const inputCls = "h-10 text-sm bg-[#0F172A] border-[#2A364F] focus-visible:ring-blue-500";

export default function EmailPanel() {
  const qc = useQueryClient();
  const [provider, setProvider] = useState("gmail");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [open, setOpen] = useState(false);

  const { data: mailboxes = [] } = useQuery({ queryKey: ["mailboxes"], queryFn: getMailboxes });

  const addMut = useMutation({
    mutationFn: () => addMailbox({ provider, email, app_password: pass }),
    onSuccess: () => {
      toast.success("Căsuță conectată ✔");
      setEmail("");
      setPass("");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Conectare eșuată"),
  });

  const delMut = useMutation({
    mutationFn: (id) => deleteMailbox(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mailboxes"] }),
  });

  const testMut = useMutation({
    mutationFn: (id) => testMailbox(id),
    onSuccess: (r) => toast.success(`Conexiune OK (${r.inbox_count} e-mailuri în inbox)`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Test eșuat"),
  });

  const pollMut = useMutation({
    mutationFn: pollEmailNow,
    onSuccess: (r) => {
      const s = r.summary || [];
      if (!s.length) toast.message("Nicio căsuță conectată");
      else
        s.forEach((x) =>
          x.error
            ? toast.error(`${x.email}: ${x.error}`)
            : toast.success(
                `${x.email}: ${x.first_run ? "gata de monitorizare" : `${x.processed} curse noi din ${x.new} e-mailuri`}`
              )
        );
      qc.invalidateQueries({ queryKey: ["offers"] });
    },
  });

  return (
    <div className="rounded-2xl border border-[#2A364F] bg-[#151C28] p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-blue-400" />
        <h3 className="font-display text-lg font-bold">Conturi E-mail</h3>
      </div>

      {mailboxes.length === 0 && !open && (
        <p className="text-sm text-slate-400">
          Conectează o căsuță (Gmail / Yahoo) ca ofertele din e-mail să intre automat.
        </p>
      )}

      {mailboxes.map((m) => (
        <div
          key={m.id}
          data-testid={`mailbox-${m.email}`}
          className="flex items-center justify-between rounded-lg bg-[#0F172A] border border-[#2A364F] px-3 py-2"
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> {m.email}
            </div>
            <div className="text-xs text-slate-500">{m.provider}</div>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-slate-400"
              onClick={() => testMut.mutate(m.id)}
              data-testid={`test-mailbox-${m.email}`}
            >
              Test
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-red-400"
              onClick={() => delMut.mutate(m.id)}
              data-testid={`delete-mailbox-${m.email}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}

      {open ? (
        <div className="space-y-2 pt-1">
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className={inputCls} data-testid="mailbox-provider-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDERS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className={inputCls}
            placeholder="adresa@gmail.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="mailbox-email-input"
          />
          <Input
            className={inputCls}
            placeholder="Parolă de aplicație (16 caractere)"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            data-testid="mailbox-password-input"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="h-10 flex-1 border-[#2A364F] bg-transparent"
              onClick={() => setOpen(false)}
            >
              Anulează
            </Button>
            <Button
              className="h-10 flex-1 bg-blue-600 hover:bg-blue-700"
              onClick={() => email && pass && addMut.mutate()}
              disabled={addMut.isPending}
              data-testid="save-mailbox-btn"
            >
              {addMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conectează"}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Ai nevoie de o „Parolă de aplicație", nu parola normală. Cere-mi pașii dacă nu știi
            cum s-o generezi.
          </p>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-10 flex-1 border-[#2A364F] bg-transparent text-sm"
            onClick={() => setOpen(true)}
            data-testid="add-mailbox-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Adaugă căsuță
          </Button>
          <Button
            className="h-10 bg-slate-700 hover:bg-slate-600 text-sm"
            onClick={() => pollMut.mutate()}
            disabled={pollMut.isPending}
            data-testid="poll-email-now-btn"
          >
            {pollMut.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-1" />
            )}
            Verifică acum
          </Button>
        </div>
      )}
    </div>
  );
}
